/* eslint-disable no-unused-vars */
import { batch } from 'react-redux';
import {
  filterAppointmentsWithinWeek,
  shouldShowAppointmentBookingOnPage,
} from '../../Components/AppointmentBooking/appointmentBooking.helpers';
import { shouldShowCpcCardOnThisPage } from '../../Components/CpcCard/cpcCard.helpers';
import validatePhone from '../../Components/PhoneNumberInput/validatePhone.helper';
import {
  calculateTransitionDistance,
  setTransitionStyling,
} from '../../Components/Transition/transition.helpers';
import {
  areConditionalQuestionsAnswered,
  getComponentValidationStatus,
  getEnvConfigForFormId,
  hasPhoneConstraint,
  hasPostcodeConstraint,
} from '../../Components/__helpers__/component.helpers';
import appointmentBookingConfig from '../../Data/Maps/appointmentBookingConfig';
import cpcCardConfig from '../../Data/Maps/cpcCardConfig';
import PreloadStatus from '../../Data/PreloadStatus';
import ValidationStatus from '../../Data/ValidationStatus';
import { exposePageChangedEvent } from '../../Helpers/events';
import { validatePostcodeSaga } from '../../Lib/redux/sagas/ValidatePostcode/validatePostcode';
import {
  setIsNextPageAppointmentBooking,
  setPreloadStatus as setPreloadStatusAppointmentBooking,
} from '../../Lib/redux/slices/appointmentBooking';
import {
  setIsNextPageCpcCard,
  setPreloadStatus as setPreloadStatusCpcCard,
} from '../../Lib/redux/slices/cpcCard';
import {
  pageChange,
  setIsFirstVisiblePostSubmissionPage,
  setIsFinalPageBeforeSubmission,
} from '../../Lib/redux/slices/navigation';
import { handleAnswerQuestionSideEffectsSaga } from '../actionCreators';
import { setIsCountdownTimerEnabled } from '../../Lib/redux/slices/formConfig';
import { setComponentValidationStatus } from '../../Lib/redux/slices/components';
import { shouldShowComponent } from '../../Helpers/conditionality';
import { getComponentsBeforePage } from '../../Helpers/components';
import {
  setLatestPartialSubmissionQuestions,
  setPartialSubmissionStatus,
} from '../../Lib/redux/slices/submission';
import SubmissionStatus from '../../Data/SubmissionStatus';
import { encode64 } from '../../Utils/utils';

/**
 * Function that is called on components to set the validation status, currently this is only called on the components that are visible on the current page
 *
 * @param {object} components - components which we need to validate
 * @param {string} countryCode - i.e. US / GB / FR
 * @param {fn} dispatch - function
 * @param {string} iFrameId
 * @param {string|int} formId
 * @returns
 */
export const validateComponents = ({
  components,
  countryCode,
  dispatch,
  iFrameId,
  formId,
}) => {
  Object.entries(components).forEach(([_key, value]) => {
    const constraints = value?.validation?.constraints;
    const includesPhoneConstraints = hasPhoneConstraint({
      component: value,
    });
    const includesPostcodeConstraints = hasPostcodeConstraint({
      component: value,
    });

    if (
      (value.component.type === 'textfield' && includesPhoneConstraints) ||
      includesPostcodeConstraints
    ) {
      if (includesPhoneConstraints || includesPostcodeConstraints) {
        const validationStatus = getComponentValidationStatus({
          component: value,
        });
        const isNotValidated =
          validationStatus === ValidationStatus.NOT_VALIDATED;
        /**
         * If the field is a telephone input and it has already been validated as true, skip validation.
         * This is because many things can trigger validation (i.e. textfield blur, navigation forwards) and
         * this prevents validating the same thing multiple times, causing UI flickering of the success icon
         */

        if (isNotValidated && includesPhoneConstraints) {
          validatePhone({
            countryCode,
            componentId: value.component.id,
            dispatch,
            iFrameId,
            constraints,
            formId,
          });
        } else if (isNotValidated && includesPostcodeConstraints) {
          dispatch(
            validatePostcodeSaga({
              countryCode,
              value: value.input.value,
              componentId: value.component.id,
              iFrameId,
            })
          );
        }
      }
    } else {
      dispatch(
        setComponentValidationStatus({
          componentId: value.component.id,
          countryCode,
          iFrameId,
          formId,
        })
      );
      // Note no dispatch of handleAnswerQuestionSideEffectsSaga as it will/should have been called on setting the components value
    }
  });
};

export const startLoadingOfCpcCardIfApplicable = ({
  formId,
  preloadStatus,
  components,
  dispatch,
  destinationPage,
}) => {
  const answersMatch = areConditionalQuestionsAnswered({
    formId,
    components,
    config: cpcCardConfig,
  });

  const shouldShowOnNextPage = shouldShowCpcCardOnThisPage(
    formId,
    components,
    destinationPage
  );

  if (answersMatch && shouldShowOnNextPage) {
    dispatch(setIsNextPageCpcCard({ isNextPageCpcCard: true }));
  } else {
    dispatch(setIsNextPageCpcCard({ isNextPageCpcCard: false }));
  }

  if (preloadStatus === PreloadStatus.NOT_STARTED && answersMatch) {
    dispatch(
      setPreloadStatusCpcCard({
        preloadStatus: PreloadStatus.WAITING_FOR_START,
      })
    );

    return true;
  }

  return false;
};

export const startLoadingAppointmentBookingIfApplicable = ({
  preloadStatus,
  components,
  dispatch,
  destinationPage,
  formId,
}) => {
  const config = getEnvConfigForFormId({
    config: appointmentBookingConfig,
    formId,
  });
  if (!config) {
    // if no config is found, this form is not part of the appointment booking flow
    return false;
  }

  /**
   * Check if the appointment booking should be shown on the page after the next page as it is shown after the next page... which should be a loader
   */
  const shouldShowOnNextPage = shouldShowAppointmentBookingOnPage({
    components,
    pageNumber: destinationPage + 1, // i.e. after a loader
    formId,
  });

  if (shouldShowOnNextPage) {
    dispatch(
      setIsNextPageAppointmentBooking({ isNextPageAppointmentBooking: true })
    );
  } else {
    dispatch(
      setIsNextPageAppointmentBooking({ isNextPageAppointmentBooking: false })
    );
  }

  if (preloadStatus === PreloadStatus.NOT_STARTED) {
    dispatch(
      setPreloadStatusAppointmentBooking({
        preloadStatus: PreloadStatus.WAITING_FOR_START,
      })
    );

    return true;
  }

  return false;
};

export const isLegalStatementVisibilityToBeChanged = ({
  isDynamicHeightEnabled,
  statement,
  isDestinationPageSubmissionPage,
  isLegalStatementRevealed,
  destinationPageHasSupplierMatchQuestion,
  currentPageHasSupplierMatchQuestion,
  currentPageIsSubmissionPage,
  submissionInProgress,
  isCurrentPagePartialSubmissionPage,
  isDestinationPagePartialSubmissionPage,
}) => {
  /*
   * If dynamic height is not enabled, we do not need to update the legal
   *  statement visibility via this route.
   */
  if (!isDynamicHeightEnabled) {
    return false;
  }
  /*
   * If there is no legal statement, we do not need to change the visibility
   *  of the legal statement.
   */
  if (!statement) {
    return false;
  }

  /**
   * If a submission is in progress, we don't want to change the visibility of the legal statement
   */
  if (submissionInProgress === true) {
    return false;
  }

  /*
   * If we are navigating to the penultimate page and the legal statement is
   * not revealed yet, we need to update the legal statement visibility.
   */
  if (isDestinationPageSubmissionPage && !isLegalStatementRevealed) {
    return true;
  }

  /*
   * If the destination page has a supplier match question and the legal statement
   * is not revealed, we need to update the legal statement visibility.
   */
  if (destinationPageHasSupplierMatchQuestion && !isLegalStatementRevealed) {
    return true;
  }

  /*
   * If the destination page should show a statement due to partial submission and the legal statement
   * is not yet revealed, we need to update the legal statement visibility.
   */
  if (isDestinationPagePartialSubmissionPage && !isLegalStatementRevealed) {
    return true;
  }

  /*
   * If the current page is showing a legal statement for any one or more of the
   * following reasons:
   * - due to it being the submission page
   * - due to it having a visible supplier match question
   * - due to it having an email only statement
   * and, additionally, the destination page does not require a legal statement,
   * we need to update the legal statement visibility
   */
  if (
    (currentPageHasSupplierMatchQuestion ||
      currentPageIsSubmissionPage ||
      isCurrentPagePartialSubmissionPage) &&
    isLegalStatementRevealed &&
    !isDestinationPageSubmissionPage &&
    !destinationPageHasSupplierMatchQuestion &&
    !isDestinationPagePartialSubmissionPage
  ) {
    return true;
  }

  /*
   * In all other cases, we do not need to update the legal statement visibility.
   */
  return false;
};

export const isNavigationBlockedOnPage = (page, pagesWithNavigationBlocks) => {
  const blocksOnPage = pagesWithNavigationBlocks[page];
  return blocksOnPage && blocksOnPage.length > 0;
};

export const isAppointmentValid = ({ appointment }) =>
  !!appointment?.selectedDate &&
  !!appointment?.selectedLocation?.name &&
  !!appointment?.selectedLocation?.address &&
  !!appointment?.selectedLocation?.id &&
  !!appointment?.selectedLocation?.locale &&
  !!appointment?.selectedLocation?.countryAlpha2;

const isPostSubmissionPage = (page, submissionPageNumber) =>
  typeof submissionPageNumber === 'number' && page > submissionPageNumber;

/**
 * Determines if a specific page should be displayed before the submission page.
 *
 * @param {number} page - The current page to evaluate.
 * @param {number} startPage - The reference page to compare against.
 * @param {number} submissionPageNumber - The page number where submission occurs.
 * @param {object} pages - An object where keys are page numbers and values are arrays of component IDs.
 * @param {object} components - An object where keys are component IDs and values are component details.
 * @param dynamicNewsletterName
 * @param {?string} phoneNumberCountryCode - The two-letter country code.
 * @param {string} subcategoryId - subcategory uuid
 * @returns {boolean} - Returns true if the page has any components that should be shown before the submission page.
 */
const isPageDueToShowBeforeSubmission = (
  page,
  startPage,
  submissionPageNumber,
  pages,
  components,
  dynamicNewsletterName,
  phoneNumberCountryCode,
  subcategoryId
) => {
  if (page <= startPage || isPostSubmissionPage(page, submissionPageNumber)) {
    return false;
  }

  const pageComponentIds = pages[page];
  return pageComponentIds.some((componentId) =>
    shouldShowComponent(
      components,
      components[`component_${componentId}`],
      dynamicNewsletterName,
      phoneNumberCountryCode,
      subcategoryId
    )
  );
};

/**
 * Checks if the given page is the last shown page before the submission page. Clicking CTA on this page will submit.
 *
 * @param {number} page - The page to evaluate.
 * @param {object} components - An object containing all components.
 * @param {object} pages - An object where keys are page numbers and values are arrays of component IDs.
 * @param {number} submissionPageNumber - The page number where submission occurs.
 * @param dynamicNewsletterName
 * @param {?string} phoneNumberCountryCode - The two-letter country code.
 * @param {string} subcategoryId
 * @returns {boolean} - Returns true if the given page is the last page before the submission page, false otherwise.
 */
export const isLastPageBeforeSubmission = ({
  page,
  components,
  pages,
  submissionPageNumber,
  dynamicNewsletterName,
  phoneNumberCountryCode,
  subcategoryId,
}) => {
  return (
    !isPostSubmissionPage(page, submissionPageNumber) &&
    !Object.keys(pages).some((iteratedPage) =>
      isPageDueToShowBeforeSubmission(
        iteratedPage,
        page,
        submissionPageNumber,
        pages,
        components,
        dynamicNewsletterName,
        phoneNumberCountryCode,
        subcategoryId
      )
    )
  );
};

/**
 *
 * Determines if a page is earlier than a specific page and also visible and post submission.
 *
 */
const IsPriorVisiblePostSubmissionPage = (
  page,
  startPage,
  submissionPageNumber,
  pages,
  components,
  dynamicNewsletterName,
  phoneNumberCountryCode,
  subcategoryId
) => {
  if (page >= startPage || !isPostSubmissionPage(page, submissionPageNumber)) {
    return false;
  }
  const pageComponentIds = pages[page];
  return pageComponentIds.some((componentId) =>
    shouldShowComponent(
      components,
      components[`component_${componentId}`],
      dynamicNewsletterName,
      phoneNumberCountryCode,
      subcategoryId
    )
  );
};

/**
 *
 * Checks if a page is the first visible page within post submission.
 * Back button should be disabled as this would attempt to navigate back to pre submission.
 *
 */
export const isFirstVisiblePostSubmissionPage = ({
  page,
  components,
  pages,
  submissionPageNumber,
  dynamicNewsletterName,
  phoneNumberCountryCode,
  subcategoryId,
}) => {
  return Boolean(
    isPostSubmissionPage(page, submissionPageNumber) &&
      !Object.keys(pages).some((iteratedPage) =>
        IsPriorVisiblePostSubmissionPage(
          iteratedPage,
          page,
          submissionPageNumber,
          pages,
          components,
          dynamicNewsletterName,
          phoneNumberCountryCode,
          subcategoryId
        )
      )
  );
};

const isTransitionToPostSubmission = ({
  currentPage,
  destinationPage,
  submissionPageNumber,
}) => {
  const currentPageIsNotPostSubmission = !isPostSubmissionPage(
    currentPage,
    submissionPageNumber
  );
  const destinationPageIsPostSubmission = isPostSubmissionPage(
    destinationPage,
    submissionPageNumber
  );
  return currentPageIsNotPostSubmission && destinationPageIsPostSubmission;
};

export const transitionPage = ({
  destinationPage,
  currentPage,
  iFrameId,
  components,
  theme,
  dispatch,
  pages,
  submissionPageNumber,
  dynamicNewsletterName,
  phoneNumberCountryCode,
  subcategoryId,
  isDestinationPagePartialSubmissionPage,
  isBounceNavigationAnimationEnabled,
}) => {
  const slideDirection = destinationPage > currentPage ? 'left' : 'right';

  const isFinalPageBeforeSubmission = isLastPageBeforeSubmission({
    page: destinationPage,
    components,
    pages,
    submissionPageNumber,
    dynamicNewsletterName,
    phoneNumberCountryCode,
    subcategoryId,
  });

  exposePageChangedEvent({
    fromPage: currentPage,
    destinationPage,
    iFrameId,
    components,
    submissionPage: submissionPageNumber,
    phoneNumberCountryCode,
    subcategoryId,
    emailConsentStatementShown: isDestinationPagePartialSubmissionPage,
    isFinalPageBeforeSubmission,
  });
  const transitionDistance = calculateTransitionDistance({
    theme,
    direction: isBounceNavigationAnimationEnabled ? 'vertical' : 'horizontal',
  });
  setTransitionStyling({
    direction: slideDirection,
    withEnter: true,
    transitionDistance,
    isBounceNavigationAnimationEnabled,
  });

  batch(() => {
    dispatch(
      setIsFinalPageBeforeSubmission({
        isFinalPageBeforeSubmission,
      })
    );
    dispatch(
      setIsFirstVisiblePostSubmissionPage({
        isFirstVisiblePostSubmissionPage: isFirstVisiblePostSubmissionPage({
          page: destinationPage,
          components,
          pages,
          submissionPageNumber,
          dynamicNewsletterName,
          phoneNumberCountryCode,
          subcategoryId,
        }),
      })
    );
    dispatch(pageChange({ currentPage: destinationPage, slideDirection }));

    if (
      isTransitionToPostSubmission({
        currentPage,
        destinationPage,
        submissionPageNumber,
      })
    ) {
      dispatch(setIsCountdownTimerEnabled({ isCountdownTimerEnabled: false }));
    }
  });
};

/**
 * shouldShowPage
 *
 * @param appointments
 * @param isAppointmentBookingPage
 * @param {*} pageNumber
 * @param pages
 * @param components
 * @param dynamicNewsletterName
 * @param phoneNumberCountryCode
 * @param subcategoryId
 * @description determine if a page should be shown or not
 * - side quest on the appointment booking, which should not be shown if we were not successful in fetching appointments
 *
 * @returns boolean
 */
const shouldShowPage = ({
  appointments,
  isAppointmentBookingPage,
  pageNumber,
  pages,
  components,
  dynamicNewsletterName,
  phoneNumberCountryCode,
  subcategoryId,
}) => {
  if (isAppointmentBookingPage && appointments && appointments?.length === 0) {
    return false;
  }

  const componentIds = pages[pageNumber];
  return componentIds.some((componentId) => {
    return shouldShowComponent(
      components,
      components[`component_${componentId}`],
      dynamicNewsletterName,
      phoneNumberCountryCode,
      subcategoryId
    );
  });
};

/**
 * determineDestinationPage
 *
 * @description determine which page to navigate to next
 *
 * @returns number
 */
export const determineDestinationPage = ({
  appointments,
  formId,
  pageNumbers,
  pageNumber,
  pages,
  components,
  dynamicNewsletterName,
  phoneNumberCountryCode,
  subcategoryId,
}) => {
  let destinationPage = pageNumber + 1;

  while (pageNumbers.includes(destinationPage)) {
    const isNextPageAppointmentBooking = shouldShowAppointmentBookingOnPage({
      components,
      formId,
      pageNumber: destinationPage,
      appointments,
    });

    const filteredAppointments = filterAppointmentsWithinWeek(appointments);

    const shouldShowDestinationPage = shouldShowPage({
      isAppointmentBookingPage: isNextPageAppointmentBooking,
      appointments: filteredAppointments,
      components,
      pageNumber: destinationPage,
      pages,
      dynamicNewsletterName,
      phoneNumberCountryCode,
      subcategoryId,
    });

    if (shouldShowDestinationPage) {
      return destinationPage;
    }

    // Otherwise, increment the destination page
    destinationPage += 1;
  }
  return pageNumber;
};

/**
 * runPendingSideEffectsOfQuestions
 *
 * Trigger the side effects of any questions which have not yet had their side effects triggered.
 */
export function runPendingSideEffectsOfQuestions({
  components,
  questionsWithSideEffectsTriggered,
  dispatch,
}) {
  Object.values(components).forEach((component) => {
    const componentId = component?.component.id;
    if (questionsWithSideEffectsTriggered[componentId] === false) {
      dispatch(handleAnswerQuestionSideEffectsSaga({ componentId }));
    }
  });
}

/**
 * triggerPartialSubmission
 *
 * Checks if there has been a change since the last partial submission, if so, starts the process of sending a partial submission.
 *
 * @param {object} preSubmissionComponents - The pre-submission components.
 * @param {number} currentPage - The current page number.
 * @param {function} dispatch - The dispatch function.
 * @param {string} latestPartialSubmissionQuestions - The latest partial submission questions in base64.
 * @returns {void}
 */
export const triggerPartialSubmission = ({
  preSubmissionComponents,
  currentPage,
  dispatch,
  latestPartialSubmissionQuestions,
}) => {
  const componentsBeforeCurrentPage = getComponentsBeforePage(
    preSubmissionComponents,
    currentPage
  );

  const componentsAsString = encode64(
    JSON.stringify(componentsBeforeCurrentPage)
  );

  if (componentsAsString !== latestPartialSubmissionQuestions) {
    // Only send a partial submission if something changed in the answers
    dispatch(
      setPartialSubmissionStatus({
        partialSubmissionStatus: SubmissionStatus.AWAITING_PARTIAL_SUBMISSION,
      })
    );
  }

  dispatch(
    setLatestPartialSubmissionQuestions({
      latestPartialSubmissionQuestions: componentsAsString,
    })
  );
};
